import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useDomainStore, useNotificationPreferencesStore } from '@/hooks/useStores';
import { Bell, BellRing, Menu } from '@/icons';
import ProfileMenu from './ProfileMenu';
import './appbar.css';

interface FamilyNavItem {
  key: string;
  label: string;
  path: string;
  isActive: (pathname: string) => boolean;
  requiresMembers?: boolean;
}

function buildFamilyNavItems(familyId: string, t: TFunction<'nav'>): FamilyNavItem[] {
  return [
    {
      key: 'overview',
      label: t('familyNav.overview'),
      path: `/families/${familyId}/overview`,
      isActive: (pathname) => pathname.includes('/overview')
    },
    {
      key: 'tree',
      label: t('familyNav.tree'),
      path: `/families/${familyId}/hybrid-pedigree/user`,
      isActive: (pathname) => pathname.includes('/hybrid-pedigree'),
      requiresMembers: true
    },
    {
      key: 'members',
      label: t('familyNav.members'),
      path: `/families/${familyId}/members`,
      isActive: (pathname) =>
        pathname.includes('/members') || pathname.includes('/people/') || pathname.includes('/pets/'),
      requiresMembers: true
    },
    {
      key: 'memories',
      label: t('familyNav.memories'),
      path: `/families/${familyId}/memories`,
      isActive: (pathname) => pathname.includes('/memories'),
      requiresMembers: true
    },
    {
      key: 'media',
      label: t('familyNav.media'),
      path: `/families/${familyId}/media`,
      isActive: (pathname) => pathname.includes('/media')
    }
  ];
}

const AppBar: React.FC = observer(() => {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const navigate = useNavigate();
  const domainStore = useDomainStore();
  const notificationPrefsStore = useNotificationPreferencesStore();
  const familyId = domainStore.selectedFamilyId;
  const memberCount = domainStore.currentFamily?.memberCount ?? 0;
  const showFamilyNav = familyId != null;
  const navItems = familyId ? buildFamilyNavItems(familyId, t) : [];
  const hasUnread = notificationPrefsStore.hasUnread;
  const disabledTitle = t('disabledTitle');

  useEffect(() => {
    if (!domainStore.user) return;
    // Refresh when navigating (skip while on Activity — mark-seen owns clearing there).
    if (location.pathname.includes('/activity')) return;
    void notificationPrefsStore.loadUnreadSummary();
  }, [domainStore.user, location.pathname, notificationPrefsStore]);

  const handleNotificationsClick = () => {
    if (!familyId) return;
    navigate(`/families/${familyId}/activity`);
  };

  const renderNavLink = (item: FamilyNavItem, className: string) => {
    const active = item.isActive(location.pathname);
    const disabled = item.requiresMembers && memberCount === 0;

    if (disabled) {
      return (
        <span
          key={item.key}
          className={`${className} ${className}--disabled`}
          title={disabledTitle}
          aria-disabled="true"
        >
          {item.label}
        </span>
      );
    }

    return (
      <Link
        key={item.key}
        to={item.path}
        className={`${className} ${active ? `${className}--active` : ''}`}
        aria-current={active ? 'page' : undefined}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header className="appbar">
      <div className="appbar__start">
        {showFamilyNav && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="appbar__hamburger" aria-label={t('openMenu')}>
                <Menu width={20} height={20} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="appbar__mobile-nav" side="bottom" align="start" sideOffset={8}>
                {navItems.map((item) => {
                  const active = item.isActive(location.pathname);
                  const disabled = item.requiresMembers && memberCount === 0;
                  if (disabled) {
                    return (
                      <span
                        key={item.key}
                        className="appbar__mobile-nav-item appbar__mobile-nav-item--disabled"
                        title={disabledTitle}
                      >
                        {item.label}
                      </span>
                    );
                  }
                  return (
                    <DropdownMenu.Item key={item.key} asChild>
                      <Link
                        to={item.path}
                        className={`appbar__mobile-nav-item ${active ? 'appbar__mobile-nav-item--active' : ''}`}
                      >
                        {item.label}
                      </Link>
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}

        <Link to="/families" className="appbar__brand" data-tour="appbar-brand">
          <span className="appbar__logo" aria-hidden />
          <span className="appbar__name">
            We<span className="appbar__name-accent">Tree</span>
          </span>
        </Link>
      </div>

      {showFamilyNav && (
        <nav className="appbar__nav" aria-label={t('familyNav.ariaLabel')} data-tour="appbar-family-nav">
          {navItems.map((item) => renderNavLink(item, 'appbar__nav-link'))}
        </nav>
      )}

      <div className="appbar__end">
        {familyId ? (
          <button
            type="button"
            className={`appbar__iconbtn${hasUnread ? ' appbar__iconbtn--unread' : ''}`}
            title={
              hasUnread
                ? t('notifications.unreadTitle', { count: notificationPrefsStore.unreadCount })
                : t('notifications.familyActivity')
            }
            aria-label={
              hasUnread
                ? t('notifications.labelUnread', { count: notificationPrefsStore.unreadCount })
                : t('notifications.label')
            }
            onClick={handleNotificationsClick}
          >
            {hasUnread ? <BellRing width={18} height={18} /> : <Bell width={18} height={18} />}
            {hasUnread && (
              <span className="appbar__iconbtn__badge" aria-hidden>
                {notificationPrefsStore.unreadCount}
              </span>
            )}
          </button>
        ) : null}
        <ProfileMenu />
      </div>
    </header>
  );
});

export default AppBar;
